import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Tooltip, Typography, Avatar } from '@mui/material';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import Chip from 'ui-component/extended/Chip';

// assets
import PendingTwoToneIcon from '@mui/icons-material/PendingTwoTone';
import AccessAlarmsTwoToneIcon from '@mui/icons-material/AccessAlarmsTwoTone';
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import DoneTwoToneIcon from '@mui/icons-material/DoneTwoTone';
import DoneAllTwoToneIcon from '@mui/icons-material/DoneAllTwoTone';
import DoNotDisturbOnTwoToneIcon from '@mui/icons-material/DoNotDisturbOnTwoTone';
import PausePresentationTwoToneIcon from '@mui/icons-material/PausePresentationTwoTone';
import PriceCheckTwoToneIcon from '@mui/icons-material/PriceCheckTwoTone';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import RunningWithErrorsTwoToneIcon from '@mui/icons-material/RunningWithErrorsTwoTone';
import FlagTwoToneIcon from '@mui/icons-material/FlagTwoTone';

export const RENDER_WORKFLOW_STATE_FORMAT0 = 0;
export const RENDER_WORKFLOW_STATE_FORMAT1 = 1;
export const RENDER_WORKFLOW_STATE_FORMAT2 = 2;
export const RENDER_WORKFLOW_STATE_FORMAT3 = 3;
export const RENDER_WORKFLOW_STATE_FORMAT4 = 4;
export const RENDER_WORKFLOW_STATE_FORMAT5 = 5;
export const RENDER_WORKFLOW_STATE_FORMAT6 = 6;

// ==============================|| RENDER WORKFLOW STATE ||============================== //

const RenderWorkflowState = ({ value, renderFormat = RENDER_WORKFLOW_STATE_FORMAT0 }) => {
    const theme = useTheme();
    const intl = useIntl();

    if (!value) return <Box />;

    switch (renderFormat) {
        case RENDER_WORKFLOW_STATE_FORMAT0:
            return intl.formatMessage({ id: `workflow-state-${value}` });
        case RENDER_WORKFLOW_STATE_FORMAT1:
            return (
                <Tooltip title={<FormattedMessage id={`workflow-state-${value}`} />}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}>
                            {value === 'created' && <PendingTwoToneIcon sx={{ color: theme.palette.grey[700] }} />}
                            {value === 'rescheduled' && <EventAvailableTwoToneIcon sx={{ color: theme.palette.warning.light }} />}
                            {value === 'running' && <EventAvailableTwoToneIcon sx={{ color: theme.palette.primary.dark }} />}
                            {value === 'delayed' && <AccessAlarmsTwoToneIcon sx={{ color: theme.palette.warning.dark }} />}
                            {value === 'completed' && <DoneTwoToneIcon sx={{ color: theme.palette.success.dark }} />}
                            {value === 'rectifying' && <RunningWithErrorsTwoToneIcon sx={{ color: theme.palette.error.light }} />}
                            {value === 'awaiting_approval' && <AccessAlarmsTwoToneIcon sx={{ color: theme.palette.warning.main }} />}
                            {value === 'approved' && <DoneAllTwoToneIcon sx={{ color: theme.palette.success.main }} />}
                            {value === 'awarded' && <PriceCheckTwoToneIcon sx={{ color: theme.palette.secondary.main }} />}
                            {value === 'rejected' && <DoNotDisturbOnTwoToneIcon sx={{ color: theme.palette.error.main }} />}
                            {value === 'paused' && <PausePresentationTwoToneIcon sx={{ color: theme.palette.grey[500] }} />}
                            {value === 'canceled' && <CancelTwoToneIcon sx={{ color: theme.palette.error.dark }} />}
                        </Avatar>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`workflow-state-${value}`} />
                        </Typography>
                    </Stack>
                </Tooltip>
            );
        case RENDER_WORKFLOW_STATE_FORMAT2:
            switch (value) {
                case 'created':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-created" />}
                            size="small"
                            variant="outlined"
                            icon={<PendingTwoToneIcon fontSize="small" />}
                            chipcolor="grey"
                        />
                    );
                case 'rescheduled':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-rescheduled" />}
                            size="small"
                            variant="outlined"
                            icon={<EventAvailableTwoToneIcon fontSize="small" />}
                            chipcolor="warning"
                        />
                    );
                case 'running':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-running" />}
                            size="small"
                            variant="outlined"
                            icon={<EventAvailableTwoToneIcon fontSize="small" />}
                            chipcolor="orange"
                        />
                    );
                case 'delayed':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-delayed" />}
                            size="small"
                            variant="outlined"
                            icon={<AccessAlarmsTwoToneIcon fontSize="small" />}
                            chipcolor="warning"
                        />
                    );
                case 'awaiting_approval':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-awaiting_approval" />}
                            size="small"
                            variant="outlined"
                            icon={<AccessAlarmsTwoToneIcon fontSize="small" />}
                            chipcolor="warning"
                        />
                    );
                case 'completed':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-completed" />}
                            size="small"
                            variant="outlined"
                            icon={<DoneTwoToneIcon fontSize="small" />}
                            chipcolor="success"
                        />
                    );
                case 'approved':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-approved" />}
                            size="small"
                            variant="outlined"
                            icon={<DoneAllTwoToneIcon fontSize="small" />}
                            chipcolor="success"
                        />
                    );
                case 'awarded':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-awarded" />}
                            size="small"
                            variant="outlined"
                            icon={<PriceCheckTwoToneIcon fontSize="small" />}
                            chipcolor="secondary"
                        />
                    );
                case 'rejected':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-rejected" />}
                            size="small"
                            variant="outlined"
                            icon={<DoNotDisturbOnTwoToneIcon fontSize="small" />}
                            chipcolor="error"
                        />
                    );
                case 'rectifying':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-rectifying" />}
                            size="small"
                            variant="outlined"
                            icon={<RunningWithErrorsTwoToneIcon fontSize="small" />}
                            chipcolor="error"
                        />
                    );
                case 'paused':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-paused" />}
                            size="small"
                            variant="outlined"
                            icon={<PausePresentationTwoToneIcon fontSize="small" />}
                            chipcolor="grey"
                        />
                    );
                case 'canceled':
                    return (
                        <Chip
                            label={<FormattedMessage id="workflow-state-canceled" />}
                            size="small"
                            variant="outlined"
                            icon={<CancelTwoToneIcon fontSize="small" />}
                            chipcolor="error"
                        />
                    );
                default:
                    return <Box />;
            }
        case RENDER_WORKFLOW_STATE_FORMAT3:
            return (
                <Tooltip title={<FormattedMessage id={`workflow-state-${value}`} />}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}>
                        {value === 'created' && <PendingTwoToneIcon sx={{ color: theme.palette.grey[700] }} />}
                        {value === 'rescheduled' && <EventAvailableTwoToneIcon sx={{ color: theme.palette.warning.light }} />}
                        {value === 'running' && <EventAvailableTwoToneIcon sx={{ color: theme.palette.primary.dark }} />}
                        {value === 'delayed' && <AccessAlarmsTwoToneIcon sx={{ color: theme.palette.warning.dark }} />}
                        {value === 'completed' && <DoneTwoToneIcon sx={{ color: theme.palette.success.dark }} />}
                        {value === 'rectifying' && <RunningWithErrorsTwoToneIcon sx={{ color: theme.palette.error.light }} />}
                        {value === 'awaiting_approval' && <AccessAlarmsTwoToneIcon sx={{ color: theme.palette.warning.main }} />}
                        {value === 'approved' && <DoneAllTwoToneIcon sx={{ color: theme.palette.success.main }} />}
                        {value === 'awarded' && <PriceCheckTwoToneIcon sx={{ color: theme.palette.secondary.main }} />}
                        {value === 'rejected' && <DoNotDisturbOnTwoToneIcon sx={{ color: theme.palette.error.main }} />}
                        {value === 'paused' && <PausePresentationTwoToneIcon sx={{ color: theme.palette.grey[500] }} />}
                        {value === 'canceled' && <CancelTwoToneIcon sx={{ color: theme.palette.error.dark }} />}
                    </Avatar>
                </Tooltip>
            );
        case RENDER_WORKFLOW_STATE_FORMAT4:
            return (
                <Tooltip
                    title={value
                        ?.filter((task) => !task?.deletedAt)
                        ?.sort((t1, t2) => (t1.positionByParent > t2.positionByParent ? 1 : -1))
                        ?.map((task, index) => (
                            <Typography key={`task${index}-statetooltip-${task?.code}-${task?.state}`} variant="inherit">{`[${
                                task?.code || ''
                            }] ${task?.name || ''}, ${intl.formatMessage({
                                id: `workflow-state-${task?.state}`
                            })}`}</Typography>
                        ))}
                >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value
                            ?.filter((task) => !task?.deletedAt)
                            ?.sort((t1, t2) => (t1.positionByParent > t2.positionByParent ? 1 : -1))
                            ?.map((task, index) => {
                                switch (task?.state) {
                                    case 'created':
                                        return (
                                            <Avatar
                                                key={`task${index}-stateavatar-${task?.code}-${task?.state}`}
                                                sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                            >
                                                <PendingTwoToneIcon sx={{ color: theme.palette.grey[700] }} />
                                            </Avatar>
                                        );
                                    case 'running':
                                        return (
                                            <Avatar
                                                key={`task${index}-stateavatar-${task?.code}-${task?.state}`}
                                                sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                            >
                                                <EventAvailableTwoToneIcon sx={{ color: theme.palette.primary.dark }} />
                                            </Avatar>
                                        );
                                    case 'delayed':
                                        return (
                                            <Avatar
                                                key={`task${index}-stateavatar-${task?.code}-${task?.state}`}
                                                sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                            >
                                                <AccessAlarmsTwoToneIcon sx={{ color: theme.palette.warning.dark }} />
                                            </Avatar>
                                        );
                                    case 'completed':
                                        return (
                                            <Avatar
                                                key={`task${index}-stateavatar-${task?.code}-${task?.state}`}
                                                sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                            >
                                                <DoneTwoToneIcon sx={{ color: theme.palette.success.dark }} />
                                            </Avatar>
                                        );
                                    case 'rectifying':
                                        return (
                                            <Avatar
                                                key={`task${index}-stateavatar-${task?.code}-${task?.state}`}
                                                sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                            >
                                                <RunningWithErrorsTwoToneIcon sx={{ color: theme.palette.secondary.dark }} />
                                            </Avatar>
                                        );
                                    default:
                                        return <Box />;
                                }
                            })}
                    </Stack>
                </Tooltip>
            );
        case RENDER_WORKFLOW_STATE_FORMAT5:
            return (
                <Tooltip
                    title={value?.map((tfus) => (
                        <Typography
                            key={`task${tfus?.taskId}-followup${tfus?.followUpId}-step${tfus?.followUpStepId}-state${tfus?.followUpStepState}-tooltip`}
                            variant="inherit"
                        >
                            {`${tfus?.taskName || ''}, ${tfus?.followUpStepName || ''}, ${intl.formatMessage({
                                id: `workflow-state-${tfus?.followUpStepState}`
                            })}`}
                        </Typography>
                    ))}
                >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value?.map((tfus) => {
                            switch (tfus?.followUpStepState) {
                                case 'created':
                                    return (
                                        <Avatar
                                            key={`task${tfus?.taskId}-followup${tfus?.followUpId}-step${tfus?.followUpStepId}-state${tfus?.followUpStepState}-avatar`}
                                            sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                        >
                                            <FlagTwoToneIcon sx={{ color: theme.palette.warning.dark }} />
                                        </Avatar>
                                    );
                                case 'confirmed':
                                    return (
                                        <Avatar
                                            key={`task${tfus?.taskId}-followup${tfus?.followUpId}-step${tfus?.followUpStepId}-state${tfus?.followUpStepState}-avatar`}
                                            sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                        >
                                            <FlagTwoToneIcon sx={{ color: theme.palette.success.dark }} />
                                        </Avatar>
                                    );
                                case 'skipped':
                                    return (
                                        <Avatar
                                            key={`task${tfus?.taskId}-followup${tfus?.followUpId}-step${tfus?.followUpStepId}-state${tfus?.followUpStepState}-avatar`}
                                            sx={{ width: 24, height: 24, bgcolor: theme.palette.grey[200] }}
                                        >
                                            <FlagTwoToneIcon sx={{ color: theme.palette.grey[500] }} />
                                        </Avatar>
                                    );
                                default:
                                    return <Box />;
                            }
                        })}
                    </Stack>
                </Tooltip>
            );
        case RENDER_WORKFLOW_STATE_FORMAT6:
            return value?.map((tfus, index) => (
                <Typography key={`taskfollowupstep${index}-statetooltip-${tfus?.id}-${tfus?.state}`} variant="inherit">
                    {`${tfus?.name || ''}, ${intl.formatMessage({ id: `workflow-state-${tfus?.state}` })}`}
                </Typography>
            ));
        default:
            return <Box />;
    }
};

RenderWorkflowState.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    renderFormat: PropTypes.oneOf([
        RENDER_WORKFLOW_STATE_FORMAT0,
        RENDER_WORKFLOW_STATE_FORMAT1,
        RENDER_WORKFLOW_STATE_FORMAT2,
        RENDER_WORKFLOW_STATE_FORMAT3,
        RENDER_WORKFLOW_STATE_FORMAT4,
        RENDER_WORKFLOW_STATE_FORMAT5,
        RENDER_WORKFLOW_STATE_FORMAT6
    ])
};

export default RenderWorkflowState;
